import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { wabaApi } from "../../lib/wabaApi";

/** Foto oficial do WhatsApp business: mostra a atual e permite trocar
 * (JPEG/PNG até 5 MB — enviada em base64, aplicada na Meta). */
export function ProfilePicture({ conn, onChanged }: { conn: any; onChanged: () => void | Promise<void> }) {
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Busca a foto atual na Meta (o backend atualiza profile_picture_url salvo).
  // Conexões antigas têm a coluna vazia — sem isso o avatar fica no placeholder.
  const { data: profile } = useQuery<{ profile_picture_url: string | null }>({
    queryKey: ["waba-profile-picture", conn.id],
    queryFn: () => wabaApi.get(`/connections/${conn.id}/profile-picture`),
    staleTime: 5 * 60_000,
  });
  const pictureUrl = profile?.profile_picture_url ?? conn.profile_picture_url;

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!["image/jpeg", "image/png"].includes(file.type)) { setError("Use JPEG ou PNG"); return; }
    if (file.size > 5 * 1024 * 1024) { setError("Máximo 5 MB"); return; }
    setError(null);
    setUploading(true);
    try {
      const b64 = await new Promise<string>((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve((r.result as string).split(",")[1]);
        r.onerror = () => reject(new Error("Falha ao ler o arquivo"));
        r.readAsDataURL(file);
      });
      await wabaApi.post(`/connections/${conn.id}/profile-picture`, { image_b64: b64, mime_type: file.type });
      qc.invalidateQueries({ queryKey: ["waba-profile-picture", conn.id] });
      qc.invalidateQueries({ queryKey: ["waba-connections"] });
      await onChanged();
    } catch (err: any) {
      setError(err?.message || "Falha ao trocar a foto");
    } finally {
      setUploading(false);
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
      <button
        onClick={() => !uploading && inputRef.current?.click()}
        title="Trocar a foto oficial do WhatsApp"
        style={{
          width: 44, height: 44, borderRadius: "50%", padding: 0, overflow: "hidden",
          border: "1px solid var(--line-strong)", background: "var(--surface-raised)",
          cursor: uploading ? "wait" : "pointer", flexShrink: 0,
          display: "flex", alignItems: "center", justifyContent: "center",
          opacity: uploading ? 0.6 : 1,
        }}
      >
        {pictureUrl
          ? <img src={pictureUrl} alt="Foto do perfil" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          : <span style={{ fontSize: 18 }}>📷</span>}
      </button>
      <span style={{ fontSize: 9, color: error ? "var(--danger)" : "var(--ink-muted)" }}>
        {uploading ? "Enviando..." : error ?? "trocar"}
      </span>
      <input ref={inputRef} type="file" accept="image/jpeg,image/png" onChange={onFile} style={{ display: "none" }} />
    </div>
  );
}
